import { type TemporalComparison, estadoFitoVistoriaConfig, riscoVistoriaConfig } from "@/mocks/vistorias";

interface TemporalComparisonPanelProps {
  comparisons: TemporalComparison[];
}

export default function TemporalComparisonPanel({ comparisons }: TemporalComparisonPanelProps) {
  const tendenciaConfig: Record<string, { badge: string; label: string; icon: string; color: string }> = {
    agravado: { badge: "bg-danger-100 text-danger-700", label: "Risco Agravado", icon: "ri-arrow-up-line", color: "text-danger-500" },
    reduzido: { badge: "bg-primary-100 text-primary-700", label: "Risco Reduzido", icon: "ri-arrow-down-line", color: "text-primary-500" },
    estavel: { badge: "bg-secondary-100 text-secondary-700", label: "Sem Alteração", icon: "ri-subtract-line", color: "text-secondary-500" },
  };

  return (
    <div className="bg-white rounded-xl border border-background-200/70 overflow-hidden">
      <div className="px-5 py-3.5 border-b border-background-100 flex items-center gap-2.5">
        <span className="w-8 h-8 rounded-lg bg-caution-50 flex items-center justify-center">
          <i className="ri-line-chart-line text-caution-500"></i>
        </span>
        <span className="text-sm font-semibold text-foreground-900 font-heading">Comparação Temporal</span>
        <span className="text-xs text-foreground-400 ml-auto">Remedição de árvores</span>
      </div>
      <div className="divide-y divide-background-100">
        {comparisons.map((comp) => {
          const tc = tendenciaConfig[comp.tendencia];
          const fitoAnt = estadoFitoVistoriaConfig[comp.vistoriaAnterior.estado] || { label: comp.vistoriaAnterior.estado };
          const fitoAtual = estadoFitoVistoriaConfig[comp.vistoriaAtual.estado] || { label: comp.vistoriaAtual.estado };
          const riscoAnt = riscoVistoriaConfig[comp.vistoriaAnterior.risco] || { label: comp.vistoriaAnterior.risco };
          const riscoAtual = riscoVistoriaConfig[comp.vistoriaAtual.risco] || { label: comp.vistoriaAtual.risco };

          return (
            <div key={comp.codigoCta} className="px-5 py-4 hover:bg-background-50/60 transition-colors">
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-accent-600 font-medium">{comp.codigoCta}</span>
                  <span className="text-sm font-semibold text-foreground-900">{comp.nomePopular}</span>
                  <span className="text-xs text-foreground-400 italic">{comp.especie.split(" ").slice(0, 2).join(" ")}</span>
                </div>
                <span className={`badge ${tc.badge}`}>
                  <i className={`${tc.icon} mr-1`}></i>{tc.label}
                </span>
              </div>

              {/* Comparison grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Previous */}
                <div className="bg-background-50 rounded-lg p-3 border border-background-200/50">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Anterior</span>
                    <span className="text-xs text-foreground-400">{new Date(comp.vistoriaAnterior.data + "T00:00:00").toLocaleDateString("pt-BR")}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <span className="text-xs text-foreground-400">DAP</span>
                      <p className="text-sm font-semibold text-foreground-700">{comp.vistoriaAnterior.dap} cm</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400">Altura</span>
                      <p className="text-sm font-semibold text-foreground-700">{comp.vistoriaAnterior.altura} m</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400">Fito</span>
                      <p className="text-sm font-semibold text-foreground-700">{fitoAnt.label}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400">Risco</span>
                      <p className="text-sm font-semibold text-foreground-700">{riscoAnt.label}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-xs text-foreground-400">Recomendação</span>
                      <p className="text-xs font-medium text-foreground-700">{comp.vistoriaAnterior.recomendacao}</p>
                    </div>
                  </div>
                </div>

                {/* Current */}
                <div className="bg-background-50 rounded-lg p-3 border border-background-200/50">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-primary-600 uppercase tracking-wider">Atual</span>
                    <span className="text-xs text-foreground-400">{new Date(comp.vistoriaAtual.data + "T00:00:00").toLocaleDateString("pt-BR")}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <span className="text-xs text-foreground-400">DAP</span>
                      <p className="text-sm font-semibold text-foreground-700">
                        {comp.vistoriaAtual.dap} cm
                        {comp.vistoriaAtual.dap !== comp.vistoriaAnterior.dap && (
                          <span className={`text-xs ml-1 ${comp.vistoriaAtual.dap > comp.vistoriaAnterior.dap ? "text-primary-600" : "text-danger-500"}`}>
                            {comp.vistoriaAtual.dap > comp.vistoriaAnterior.dap ? "+" : ""}{comp.vistoriaAtual.dap - comp.vistoriaAnterior.dap}
                          </span>
                        )}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400">Altura</span>
                      <p className="text-sm font-semibold text-foreground-700">
                        {comp.vistoriaAtual.altura} m
                        {comp.vistoriaAtual.altura !== comp.vistoriaAnterior.altura && (
                          <span className={`text-xs ml-1 ${comp.vistoriaAtual.altura > comp.vistoriaAnterior.altura ? "text-primary-600" : "text-danger-500"}`}>
                            {comp.vistoriaAtual.altura > comp.vistoriaAnterior.altura ? "+" : ""}{(comp.vistoriaAtual.altura - comp.vistoriaAnterior.altura).toFixed(1)}
                          </span>
                        )}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400">Fito</span>
                      <p className="text-sm font-semibold text-foreground-700">{fitoAtual.label}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400">Risco</span>
                      <p className="text-sm font-semibold text-foreground-700">{riscoAtual.label}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-xs text-foreground-400">Recomendação</span>
                      <p className="text-xs font-medium text-foreground-700">{comp.vistoriaAtual.recomendacao}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}